import { createSlice } from '@reduxjs/toolkit';
import {
  basketWithIdThunk,
  cancelFullBasketThunk,
  deleteSecurityThunk,
  getAllBasketsThunk,
  getClientsAndFundsThunk,
  getInventoryDataThunk,
  holdInventoryDataThunk,
  saveBasketThunk,
} from './SwapBasketsActions';
import {
  getClientAndFundIds,
  isDraftBasketOpen,
  transformAvailableBasketsData,
  transformInventoryData,
} from './SwapBasketsHelper';
import { cloneDeep } from 'lodash';

export interface SwapBasketsData {
  availableBaskets: any[];
  basketInputData: any;
  clientsData: any;
  draftBasketDetails: any;
  inventoryData: any[];
  clientsDataLoading: boolean;
  inventoryDataLoading: boolean;
  draftBasketDetailsLoading: boolean;
  availableBasketsLoading: boolean;
  cancelSecurityLoading: Record<string, boolean>;
  hasReadOnlyAccess: boolean;
}

const initialState: SwapBasketsData = {
  availableBaskets: [],
  basketInputData: null,
  clientsData: null,
  draftBasketDetails: null,
  inventoryData: [],
  clientsDataLoading: false,
  inventoryDataLoading: false,
  draftBasketDetailsLoading: false,
  availableBasketsLoading: false,
  cancelSecurityLoading: {},
  hasReadOnlyAccess: true,
};

const swapBasketsSlice = createSlice({
  name: 'Swap Baskets',
  initialState,
  reducers: {
    setBasketInputData: (state, action) => {
      state.basketInputData = action.payload;
    },
    resetSwapBasketStates: (state) => {
      state.basketInputData = null;
      state.draftBasketDetails = null;
      state.inventoryData = [];
      state.cancelSecurityLoading = {};
    },
    checkReadOnlyAccess: (state, action) => {
      state.hasReadOnlyAccess = action.payload;
    },
    resetInventoryData: (state) => {
      state.inventoryData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInventoryDataThunk.pending, (state) => {
        if (
          isDraftBasketOpen(
            cloneDeep(state.draftBasketDetails),
            cloneDeep(state.basketInputData),
            cloneDeep(state.clientsData),
          )
        ) {
          state.draftBasketDetails = null;
        }
        state.inventoryData = [];
        state.inventoryDataLoading = true;
      })
      .addCase(getInventoryDataThunk.fulfilled, (state, action) => {
        const data = transformInventoryData(action.payload?.items, cloneDeep(state.clientsData));
        state.inventoryData = data;
        state.inventoryDataLoading = false;
      })
      .addCase(getInventoryDataThunk.rejected, (state) => {
        state.inventoryData = [];
        state.inventoryDataLoading = false;
      })
      .addCase(holdInventoryDataThunk.pending, (state) => {
        state.draftBasketDetailsLoading = true;
      })
      .addCase(holdInventoryDataThunk.fulfilled, (state) => {
        state.draftBasketDetailsLoading = false;
      })
      .addCase(holdInventoryDataThunk.rejected, (state) => {
        state.draftBasketDetailsLoading = false;
      })
      .addCase(basketWithIdThunk.pending, (state) => {
        state.draftBasketDetails = { items: null };
        state.draftBasketDetailsLoading = true;
        state.inventoryData = [];
      })
      .addCase(basketWithIdThunk.fulfilled, (state, action) => {
        if (action.payload?.data?.basket) {
          const data = action.payload.data.basket;
          const { clientId, fundId } = getClientAndFundIds(data, cloneDeep(state.clientsData));
          state.basketInputData = { clientId, fundId };
          state.draftBasketDetails = data;
        }
        state.draftBasketDetailsLoading = false;
      })
      .addCase(basketWithIdThunk.rejected, (state) => {
        state.draftBasketDetails = null;
        state.draftBasketDetailsLoading = false;
      })
      .addCase(cancelFullBasketThunk.pending, (state) => {
        state.draftBasketDetailsLoading = true;
      })
      .addCase(cancelFullBasketThunk.fulfilled, (state) => {
        state.draftBasketDetails = null;
        state.draftBasketDetailsLoading = false;
      })
      .addCase(cancelFullBasketThunk.rejected, (state) => {
        state.draftBasketDetailsLoading = false;
      })
      .addCase(saveBasketThunk.pending, (state) => {
        state.draftBasketDetailsLoading = true;
      })
      .addCase(saveBasketThunk.fulfilled, (state) => {
        state.draftBasketDetails = null;
        state.draftBasketDetailsLoading = false;
      })
      .addCase(saveBasketThunk.rejected, (state) => {
        state.draftBasketDetailsLoading = false;
      })
      .addCase(getAllBasketsThunk.pending, (state) => {
        state.availableBasketsLoading = true;
      })
      .addCase(getAllBasketsThunk.fulfilled, (state, action) => {
        state.availableBaskets = transformAvailableBasketsData(
          action.payload?.data?.allAvailableBaskets,
        );
        state.availableBasketsLoading = false;
      })
      .addCase(getAllBasketsThunk.rejected, (state) => {
        state.availableBasketsLoading = false;
      })
      .addCase(getClientsAndFundsThunk.pending, (state) => {
        state.clientsDataLoading = true;
      })
      .addCase(getClientsAndFundsThunk.fulfilled, (state, action) => {
        state.clientsData = action.payload?.data?.allClients;
        state.clientsDataLoading = false;
      })
      .addCase(getClientsAndFundsThunk.rejected, (state) => {
        state.clientsDataLoading = false;
      })
      .addCase(deleteSecurityThunk.pending, (state, action) => {
        const securityId = (action.meta.arg as { securityId?: string | number })?.securityId;
        if (securityId !== undefined && securityId !== null) {
          const key = String(securityId);
          state.cancelSecurityLoading = {
            ...state.cancelSecurityLoading,
            [key]: true,
          };
        }
      })
      .addCase(deleteSecurityThunk.fulfilled, (state, action) => {
        const securityId = (action.meta.arg as { securityId?: string | number })?.securityId;
        if (securityId !== undefined && securityId !== null) {
          const key = String(securityId);
          const rest = {};
          for (const k in state.cancelSecurityLoading) {
            if (k !== key) rest[k] = state.cancelSecurityLoading[k];
          }
          state.cancelSecurityLoading = rest;
        }
      })
      .addCase(deleteSecurityThunk.rejected, (state, action) => {
        const securityId = (action.meta.arg as { securityId?: string | number })?.securityId;
        if (securityId !== undefined && securityId !== null) {
          const key = String(securityId);
          const rest = {};
          for (const k in state.cancelSecurityLoading) {
            if (k !== key) rest[k] = state.cancelSecurityLoading[k];
          }
          state.cancelSecurityLoading = rest;
        }
      });
  },
});

export const {
  setBasketInputData,
  resetSwapBasketStates,
  checkReadOnlyAccess,
  resetInventoryData,
} = swapBasketsSlice.actions;
export default swapBasketsSlice.reducer;
